// scheduling/pages/StaffSuggestions.jsx
// Ranked staff suggestions for a given job, scored by percentile-based experience.
// P5: Job context card, P6: Assignment banner, P7: Staff interests in breakdown.

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchSuggestions, fetchJobs, fetchInterests, fetchJobsWIP, fetchStaff, fetchCalendars, fetchPTO, fetchHolidays, fetchGlobalAssignments, fetchSchedule } from '../services/schedData';
import { jobBucketOf } from '../utils/genTrack';
import { BRAND, EXP_WEIGHTS, JOB_TYPE_COLORS, getExpLevel, baseSuggestionFilter, SUGGESTION_LEVEL_OPTIONS, SUGGESTION_LEADERSHIP_LEVELS } from '../utils/schedConstants';
import MatchBadge from '../components/MatchBadge';
import NicheGapFlag from '../components/NicheGapFlag';
import GenTrackFlag from '../components/GenTrackFlag';
import { useGenTrackBands } from '../hooks/useGenTrackBands';
import { useUrlState, csvOfStrings } from '../../hooks/useUrlState';
import { toISO, makeGetDayCap, buildPtoByStaffDay, buildHolidayByDate, buildDayHoursByStaff, computeStaffWeeklyUtil } from '../utils/capacity';
import { distributeEvenlyQuarters } from '../utils/optimizeHours';
import { SENTINEL_JOB_ID, TOUR_SUGGESTION_JOB, TOUR_SUGGESTION_CANDIDATES } from '../../components/tour/tourDemoData';

function pctLabel(pct) { return getExpLevel(pct).label; }
function pctColor(pct) { return getExpLevel(pct).color; }

// Availability heatmap date labels. weekStart comes back as 'YYYY-MM-DD'; parse at
// noon so the M/D / "Mon D" formatting doesn't shift across the UTC boundary.
function fmtMD(iso) { if (!iso) return ''; const [, m, d] = iso.slice(0, 10).split('-'); return `${parseInt(m, 10)}/${parseInt(d, 10)}`; }
function fmtMon(iso) { if (!iso) return ''; return new Date(iso.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }

function ScoreBar({ value, max, color }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="sched-sug-scorebar">
      <div className="sched-sug-scorebar-track">
        <div className="sched-sug-scorebar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="sched-sug-scorebar-val">{value}</span>
    </div>
  );
}

export default function StaffSuggestions() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [sugJob, setSugJob] = useState(null); // job metadata from suggestions API
  const [candidates, setCandidates] = useState([]);
  const [filteredCount, setFilteredCount] = useState(0); // hard-filtered (e.g., Yellow Book)
  const [scoringWeights, setScoringWeights] = useState({ experience: 60, interest: 25 });
  const [jobWipTotal, setJobWipTotal] = useState(0);
  const [interestsByStaff, setInterestsByStaff] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  // Gen Track tax/assurance bands, for the "Needs more Tax/Assurance" badge
  // (Trello #309). The hook fetches once per page and shares the result with the
  // five other candidate surfaces, so the ~600ms pe.TranWIP scan happens once no
  // matter how many of them you visit. Never rejects: no badge is strictly better
  // than an error on a page whose job is ranking candidates.
  const { bandByStaff, lists: genTrackLists } = useGenTrackBands();
  // Filters live in the URL (?tier, ?level, ?available) for shareable / navbot deeplinks.
  const [filterTier, setFilterTier] = useUrlState('tier', 'all', { validate: ['all', 'client', 'niche', 'serviceline', 'none'] });
  // Multi-select level filter (raw level names, e.g. ?level=Manager,Supervisor). Empty = all.
  const [filterLevel, setFilterLevel] = useUrlState('level', [], { serialize: csvOfStrings.serialize, deserialize: csvOfStrings.deserialize });
  const [levelMenuOpen, setLevelMenuOpen] = useState(false);
  const [selected, setSelected] = useState(new Set()); // P6: selected staff IDs for batch assign
  const [availByStaff, setAvailByStaff] = useState({}); // staffId → [{ weekStart, capacity, scheduled, pct }]
  // Drives the heatmap window caption + per-row assignment bracket. `spanned` is the
  // assignment's true week count (null when no range); `numWeeks` is what's displayed
  // (spanned padded to a 5-wk min, capped at 13). assign*ISO are the raw selected dates.
  const [availMeta, setAvailMeta] = useState({ rangeMode: false, windowKind: 'default', numWeeks: 5, spanned: null, assignStartISO: null, assignEndISO: null });
  const [showOnlyAvailable, setShowOnlyAvailable] = useUrlState('available', false);
  const [includeLeadership, setIncludeLeadership] = useUrlState('leadership', false); // surface Sr Mgr/Principal/Member (hidden by default)
  const [showAllOffices, setShowAllOffices] = useUrlState('alloffices', false); // on-site override: show staff from all offices
  const [returnParam] = useUrlState('return', ''); // 'grid' when opened from the schedule modal (round-trip)
  // Alternate window source (no grid round-trip): My Jobs / Dashboard "Find Staff"
  // pass ?we=<deadlineISO> (+ optional ?ws=) to window the heatmap to today→deadline.
  const [weParam] = useUrlState('we', '');
  const [wsParam] = useUrlState('ws', '');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      // Guided-tour demo: the sentinel job renders curated sample candidates so the
      // scoring UI is populated for everyone, with no API calls (all view-only).
      if (parseInt(jobId) === SENTINEL_JOB_ID) {
        setCandidates(TOUR_SUGGESTION_CANDIDATES);
        setSugJob(TOUR_SUGGESTION_JOB);
        setJob(TOUR_SUGGESTION_JOB);
        setScoringWeights({ experience: 60, interest: 25 });
        setLoading(false);
        return;
      }
      try {
        const [sugData, jobData] = await Promise.all([
          fetchSuggestions(parseInt(jobId)),
          fetchJobs({ jobId: parseInt(jobId) }),
        ]);
        if (cancelled) return;
        // New API returns { candidates, job, filteredCount } or legacy array
        const cands = sugData?.candidates || (Array.isArray(sugData) ? sugData : []);
        setCandidates(cands);
        setSugJob(sugData?.job || null);
        setFilteredCount(sugData?.filteredCount || 0);
        if (sugData?.weights) setScoringWeights(sugData.weights);
        const theJob = Array.isArray(jobData) && jobData.length > 0 ? jobData[0] : null;
        setJob(theJob);

        // Fetch WIP total for this job to compute remaining budget
        if (theJob) {
          try {
            const wipData = await fetchJobsWIP({ jobId: theJob.JobId });
            const total = (wipData || []).reduce((s, w) => s + (w.TotalHours || 0), 0);
            if (!cancelled) setJobWipTotal(total);
          } catch { /* non-blocking */ }
        }

        // P7: Fetch interests for top candidates (first 20)
        const topIds = cands.slice(0, 20).map(c => c.StaffId);
        const interestMap = {};
        await Promise.all(topIds.map(async (sid) => {
          try {
            const ints = await fetchInterests(sid);
            if (ints?.length > 0) interestMap[sid] = ints;
          } catch { /* ignore */ }
        }));
        if (!cancelled) setInterestsByStaff(interestMap);

        // Availability window. Default: the next 5 weeks from today. When the page
        // is opened from the grid edit modal (?return=grid), the stashed draft carries
        // the assignment's date range — anchor the heatmap to that span instead (min 5
        // weeks so a short assignment still shows a useful horizon; backend caps at 13).
        const allIds = cands.map(c => c.StaffId);
        const snapMonday = (dt) => { const m = new Date(dt); const dw = m.getDay(); m.setDate(m.getDate() - (dw === 0 ? 6 : dw - 1)); m.setHours(0, 0, 0, 0); return m; };
        let rangeMode = false, rangeEnd = null, windowKind = 'default';
        let anchor = new Date();
        let assignStartISO = null, assignEndISO = null;
        if (returnParam === 'grid') {
          try {
            const draft = JSON.parse(sessionStorage.getItem('pendingScheduleDraft') || '{}');
            if (draft.start) {
              rangeMode = true; windowKind = 'assignment';
              anchor = new Date(draft.start + 'T12:00:00');
              assignStartISO = draft.start;
              if (draft.end) { rangeEnd = new Date(draft.end + 'T12:00:00'); assignEndISO = draft.end; }
            }
          } catch { /* fall back to the default next-5-weeks window */ }
        } else if (weParam) {
          // No grid round-trip: caller passed an explicit window end (My Jobs / Dashboard
          // "Find Staff" → today → job deadline). Optional ?ws= overrides the start.
          rangeMode = true; windowKind = 'deadline';
          if (wsParam) { anchor = new Date(wsParam + 'T12:00:00'); assignStartISO = wsParam; }
          rangeEnd = new Date(weParam + 'T12:00:00');
          assignEndISO = weParam;
        }
        const monday = snapMonday(anchor);
        // spanned = the window's true week count (null when we have no end date).
        // numWeeks = what we actually display: spanned padded to a 5-wk min, capped at 13.
        let spanned = null, numWeeks = 5;
        if (rangeMode && rangeEnd) {
          spanned = Math.max(1, Math.round((snapMonday(rangeEnd) - monday) / (7 * 86400000)) + 1); // ≥1 even if deadline already passed
          numWeeks = Math.min(Math.max(spanned, 5), 13);
        }
        const startDate = monday.toISOString().slice(0, 10);
        if (!cancelled) setAvailMeta({ rangeMode, windowKind, numWeeks, spanned, assignStartISO, assignEndISO });
        try {
          // Availability is computed client-side with the shared capacity util —
          // the SAME logic as the grid's People-view capacity bar — so the heatmap
          // is PTO / holiday / global-assignment aware and never diverges from the grid.
          const weekMondays = [];
          for (let i = 0; i < numWeeks; i++) {
            const d = new Date(monday); d.setDate(d.getDate() + i * 7);
            weekMondays.push(toISO(d));
          }
          const lastDay = new Date(monday); lastDay.setDate(lastDay.getDate() + numWeeks * 7 - 1);
          const winEnd = toISO(lastDay);
          const years = new Set();
          for (let y = monday.getFullYear(); y <= lastDay.getFullYear(); y++) years.add(y);
          const [staffRows, calRows, ptoRows, holidayRows, gaRows, schedRows] = await Promise.all([
            fetchStaff(),
            fetchCalendars(),
            fetchPTO({ startDate, endDate: winEnd }),
            fetchHolidays(),
            fetchGlobalAssignments(),
            fetchSchedule({ startDate, endDate: winEnd }),
          ]);
          if (cancelled) return;
          const getDayCap = makeGetDayCap({
            staff: staffRows || [],
            globalCalendars: calRows || [],
            ptoByStaffDay: buildPtoByStaffDay(ptoRows),
            holidayByDate: buildHolidayByDate(holidayRows, years),
          });
          const dayHoursByStaff = buildDayHoursByStaff(schedRows, gaRows, staffRows);
          const rows = computeStaffWeeklyUtil({ staffIds: allIds, weekMondays, getDayCap, dayHoursByStaff });
          const map = {};
          for (const r of rows) map[r.staffId] = r.weeks;
          if (!cancelled) setAvailByStaff(map);
        } catch { /* non-blocking */ }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [jobId, returnParam, weParam, wsParam]);

  // Level picklist options ({ label, levels }). Non-leadership always; leadership only
  // when "include leadership" is on, prepended in descending seniority so the whole list
  // stays ordered most-senior-first. Senior Manager + Principal are the same title → one
  // combined option that selects both.
  const levelOptions = useMemo(() => {
    const base = SUGGESTION_LEVEL_OPTIONS.map(l => ({ label: l, levels: [l] }));
    if (!includeLeadership) return base;
    return [
      { label: 'Member', levels: ['Member'] },
      { label: 'Senior Manager / Principal', levels: ['Senior Manager', 'Principal'] },
      { label: 'Consultant', levels: ['Consultant'] },
      ...base,
    ];
  }, [includeLeadership]);
  // If leadership is turned off, drop any leadership levels from the selection so a
  // stale pick can't silently filter everyone out.
  useEffect(() => {
    if (includeLeadership) return;
    setFilterLevel(prev => (prev.some(l => SUGGESTION_LEADERSHIP_LEVELS.includes(l))
      ? prev.filter(l => !SUGGESTION_LEADERSHIP_LEVELS.includes(l)) : prev));
  }, [includeLeadership, setFilterLevel]);
  const toggleLevelOption = useCallback((opt) => {
    setFilterLevel(prev => {
      const allOn = opt.levels.every(l => prev.includes(l));
      return allOn ? prev.filter(l => !opt.levels.includes(l)) : [...prev, ...opt.levels.filter(l => !prev.includes(l))];
    });
  }, [setFilterLevel]);
  // Department + leadership auto-filtering lives in baseSuggestionFilter
  // (shared with the grid modal's quick-suggest so the two rankings agree).

  // On-site jobs restrict suggestions to the job's office (OfficeCode); "Show all
  // offices" overrides it here (the modal + conflicts pickers are always hard-filtered).
  const onSiteOfficeCode = (job?.IsOnSite && job?.OfficeCode && !showAllOffices) ? job.OfficeCode : null;

  // How many of the displayed weeks the assignment ACTUALLY covers — i.e. the cells
  // the heatmap draws the "Assignment" bracket under. The window is padded out to a
  // 5-week minimum for context (numWeeks above), so for a one-week assignment 4 of
  // the 5 cells are padding. Both the Available-Only filter and the heatmap render
  // must agree on where the bracket ends; they used to compute it separately, and
  // the filter simply didn't, which is what let a solid-booked staffer through.
  // 0 = no bracket (no range, or a deadline past the 13-week display cap) → the
  // whole window is meaningful and every cell counts.
  const inRangeCountFor = useCallback((weeks) => {
    if (!availMeta.rangeMode || availMeta.spanned == null) return 0;
    const deadlineBeyondCutoff = availMeta.windowKind === 'deadline' && availMeta.spanned > weeks.length;
    return deadlineBeyondCutoff ? 0 : Math.min(availMeta.spanned, weeks.length);
  }, [availMeta]);

  const filtered = useMemo(() => {
    // Department + leadership auto-filters (leadership toggle defaults off) + on-site office
    let list = baseSuggestionFilter(candidates, job?.ServiceDesc, { includeLeadership, onSiteOfficeCode });
    // Manual tier filter
    if (filterTier !== 'all') list = list.filter(c => c.matchTier === filterTier);
    // Manual level filter (multi-select, exact match). Empty selection = all levels.
    if (filterLevel.length) list = list.filter(c => filterLevel.includes(c.Level));
    // Availability filter — hide staff who are booked solid (red, >80% utilized /
    // <20% free) in EVERY week of the ASSIGNMENT. Keep anyone with at least one
    // not-red week in that span.
    //
    // Scoped to the bracketed weeks, not the whole heatmap: the window is padded to
    // a 5-week minimum, so scanning every cell meant a staffer 100% booked for the
    // one week being staffed still passed because they were free a month later. The
    // filter said "available", the grid then said "overscheduled" (bug report #53).
    if (showOnlyAvailable) {
      list = list.filter(c => {
        const weeks = availByStaff[c.StaffId];
        if (!weeks) return true; // no data yet — don't hide
        const n = inRangeCountFor(weeks);
        return (n > 0 ? weeks.slice(0, n) : weeks).some(w => w.pct <= 80);
      });
    }
    return list;
  }, [candidates, job, includeLeadership, onSiteOfficeCode, filterTier, filterLevel, showOnlyAvailable, availByStaff, inRangeCountFor]);

  // P6: Toggle selection
  const toggleSelect = useCallback((staffId, e) => {
    e.stopPropagation();
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(staffId)) next.delete(staffId); else next.add(staffId);
      return next;
    });
  }, []);

  // Round-trip targets: opened from the grid schedule modal (?return=grid) or the
  // backlog Schedule chooser (?return=chooser). Picking staff writes them into the
  // matching draft key and returns to that caller with the modal/chooser reopened.
  const returnToGrid = returnParam === 'grid';
  const returnToChooser = returnParam === 'chooser';
  const isRoundTrip = returnToGrid || returnToChooser;
  const roundTripKey = returnToChooser ? 'pendingChooserDraft' : 'pendingScheduleDraft';
  // Where to return after a chooser round-trip — origin-aware (backlog sub-tab vs the
  // My Jobs tab + job sidebar). Reads the draft ScheduleChooserModal stashed on the way out.
  const chooserReturnUrl = useMemo(() => {
    if (!returnToChooser) return '/schedule/dashboard?tab=backlog&backlog=mytime';
    let d = {};
    try { d = JSON.parse(sessionStorage.getItem('pendingChooserDraft') || '{}'); } catch { /* ignore */ }
    // Reporting section (Unscheduled Hours). No job param is needed: that report
    // rebuilds the chooser entirely from pendingChooserDraft, which already carries the
    // job, its remaining hours and the picks.
    if (d.origin === 'report') return '/schedule/reporting?tab=unscheduled';
    if (d.origin === 'myjobs' && d.jobId != null) return `/schedule/dashboard?tab=myjobs&job=${d.jobId}`;
    return `/schedule/dashboard?tab=backlog&backlog=${d.sub || 'mytime'}`;
  }, [returnToChooser]);

  // P6: Navigate to grid with assignment context
  const handleConfirmAssign = useCallback(() => {
    if (!job || selected.size === 0) return;
    // Round-trip back to the backlog Schedule chooser (?return=chooser): write the
    // picks into pendingChooserDraft and return to the dashboard, which reopens the
    // chooser (its own draft key — never collides with the grid's pendingScheduleDraft).
    if (returnToChooser) {
      try {
        const raw = sessionStorage.getItem('pendingChooserDraft');
        const draft = raw ? JSON.parse(raw) : {};
        draft.pickedStaffIds = [...selected];
        // Carry names/levels so the chooser can render picks it didn't already list.
        draft.pickedStaffMeta = [...selected].map(sid => {
          const c = candidates.find(x => x.StaffId === sid);
          return { staffId: sid, name: c?.Name || `#${sid}`, level: c?.Level || '' };
        });
        sessionStorage.setItem('pendingChooserDraft', JSON.stringify(draft));
      } catch { /* ignore */ }
      navigate(chooserReturnUrl);
      return;
    }
    // Multi round-trip from the grid modal: write the picks into the draft and return
    // to the open modal (multi-staff mode) instead of the dashboard pendingAssignment flow.
    if (returnParam === 'grid') {
      try {
        const raw = sessionStorage.getItem('pendingScheduleDraft');
        const draft = raw ? JSON.parse(raw) : {};
        draft.pickedStaffIds = [...selected];
        sessionStorage.setItem('pendingScheduleDraft', JSON.stringify(draft));
      } catch { /* ignore */ }
      navigate('/schedule/grid');
      return;
    }
    // Store assignment context in sessionStorage so the grid can pick it up. For a
    // MULTI-staff pick, emit perStaff with an EVEN SPLIT of the remaining budget so the
    // grid's perStaff branch capacity-optimizes each share — instead of the legacy branch
    // that seeds every staffer with the FULL remaining (the Item 3 even-distribution bug).
    const ids = [...selected];
    const remainingBudget = Math.max(0, Math.round(((job.BudgetHours || 0) - jobWipTotal - (job.scheduledHrs || 0)) * 100) / 100) || (job.BudgetHours || 40);
    const assignData = {
      jobId: job.JobId,
      jobName: job.Name,
      clientName: job.ClientName,
      staffIds: ids,
      staffNames: ids.map(sid => candidates.find(c => c.StaffId === sid)?.Name || ''),
      suggestedHours: remainingBudget,
      budgetHours: job.BudgetHours || 0,
      wipTotal: jobWipTotal,
      scheduledHours: job.scheduledHrs || 0,
    };
    if (ids.length > 1) {
      const shares = distributeEvenlyQuarters(remainingBudget, ids.length);
      assignData.perStaff = ids.map((sid, i) => ({ staffId: sid, hours: shares[i] }));
    }
    sessionStorage.setItem('pendingAssignment', JSON.stringify(assignData));
    navigate('/schedule/grid');
  }, [job, selected, candidates, navigate, returnParam, returnToChooser, chooserReturnUrl, jobWipTotal]);

  const backToCaller = useCallback(() => {
    if (returnToGrid) navigate('/schedule/grid');
    else if (returnToChooser) navigate(chooserReturnUrl);
    else navigate(-1);
  }, [returnToGrid, returnToChooser, chooserReturnUrl, navigate]);
  // Single vs multi mode. The chooser is always a team (multi) pick; the grid
  // carries its own single/multi flag in its draft.
  const returnMulti = useMemo(() => {
    if (returnToChooser) return true;
    if (!returnToGrid) return false;
    try { return !!JSON.parse(sessionStorage.getItem('pendingScheduleDraft') || '{}').isMultiStaff; } catch { return false; }
  }, [returnToGrid, returnToChooser]);
  // The team the caller already had (pre-existing members) — pre-select them and
  // label the ribbon by what's being ADDED on this page (F1/F2).
  const priorTeamIds = useMemo(() => {
    if (!isRoundTrip) return [];
    try { return JSON.parse(sessionStorage.getItem(roundTripKey) || '{}').priorTeamIds || []; } catch { return []; }
  }, [isRoundTrip, roundTripKey]);
  useEffect(() => {
    if (returnMulti && priorTeamIds.length) setSelected(new Set(priorTeamIds));
  }, [returnMulti, priorTeamIds]);
  const priorTeamSet = useMemo(() => new Set(priorTeamIds), [priorTeamIds]);
  const newPickIds = useMemo(() => [...selected].filter(id => !priorTeamSet.has(id)), [selected, priorTeamSet]);
  const existingPickIds = useMemo(() => [...selected].filter(id => priorTeamSet.has(id)), [selected, priorTeamSet]);
  const nameOf = (sid) => candidates.find(c => c.StaffId === sid)?.Name || `#${sid}`;
  const pickStafferForGrid = useCallback((staffId) => {
    try {
      const raw = sessionStorage.getItem('pendingScheduleDraft');
      if (raw) {
        const draft = JSON.parse(raw);
        draft.pickedStaffId = staffId;
        sessionStorage.setItem('pendingScheduleDraft', JSON.stringify(draft));
      }
    } catch { /* ignore */ }
    navigate('/schedule/grid');
  }, [navigate]);

  // Which side does THIS job count toward? The code/name lists come from the server
  // response so they cannot drift from what the aggregation used.
  const jobBucket = useMemo(() => jobBucketOf(job, genTrackLists), [job, genTrackLists]);

  if (loading) return <div className="sched-home-loading"><div className="sched-home-loading-text">Loading suggestions...</div></div>;
  if (error) return <div className="sched-placeholder"><h2>Error</h2><p style={{ color: '#DC2626' }}>{error}</p></div>;

  const typeColor = job ? (JOB_TYPE_COLORS[job.ServiceDesc] || JOB_TYPE_COLORS[job.JobType] || { bg: '#E5E7EB', text: '#374151' }) : {};

  return (
    <div className="sched-sug-root">

      {/* P5: Job Context Card */}
      {job && (
        <div className={`sched-sug-context${returnToGrid ? ' sched-sug-context--return' : ''}`}>
          <div className="sched-sug-context-left">
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', fontWeight: 700, letterSpacing: '0.05em', marginBottom: 4 }}>FINDING STAFF FOR</div>
            <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 2 }}>{job.ClientName}</div>
            <div style={{ fontSize: 13, opacity: 0.8 }}>{job.Name}</div>
          </div>
          <div className="sched-sug-context-right">
            <div className="sched-sug-context-item">
              <div className="sched-sug-context-label">Type</div>
              <span className="sched-type-tag" style={{ background: typeColor.bg, color: typeColor.text }}>
                {job.ServiceDesc || job.JobType || 'Other'}
              </span>
            </div>
            <div className="sched-sug-context-item">
              <div className="sched-sug-context-label">Niche</div>
              <div className="sched-sug-context-value">{sugJob?.nicheName || job.ClientIndustry || job.DepartmentCode || '\u2014'}</div>
            </div>
            <div className="sched-sug-context-item">
              <div className="sched-sug-context-label">Budget</div>
              <div className="sched-sug-context-value">{job.BudgetHours || 0}h</div>
            </div>
            <div className="sched-sug-context-item">
              <div className="sched-sug-context-label">Internal Due Date</div>
              <div className="sched-sug-context-value">
                {job.Deadline ? new Date(job.Deadline.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '\u2014'}
              </div>
            </div>
            {job.PartnerName && (
              <div className="sched-sug-context-item">
                <div className="sched-sug-context-label">Partner</div>
                <div className="sched-sug-context-value">{job.PartnerName}</div>
              </div>
            )}
          </div>
          <button className="sched-sug-back" style={{ position: 'absolute', top: 12, left: 12, background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)' }}
            onClick={backToCaller}>{returnToGrid ? '← Back to schedule' : returnToChooser ? '← Back to backlog' : '← Back'}</button>
        </div>
      )}

      {/* Scoring legend */}
      <div className="sched-sug-legend">
        <span className="sched-sug-legend-title">Fit Score</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 8, height: 8, background: BRAND.deepTeal, borderRadius: 2 }} /> Client &times;{EXP_WEIGHTS.client}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 8, height: 8, background: BRAND.oceanBlue, borderRadius: 2 }} /> Niche &times;{EXP_WEIGHTS.niche}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 8, height: 8, background: BRAND.sageGreen, borderRadius: 2 }} /> Service Line &times;{EXP_WEIGHTS.serviceline}
        </span>
        {sugJob?.yellowBookRequired && (
          <span style={{ marginLeft: 8, background: '#FEF3C7', color: '#92400E', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4 }}>
            Yellow Book Required {filteredCount > 0 && <>&middot; {filteredCount} filtered</>}
          </span>
        )}
        {sugJob?.nicheName && (
          <span style={{ marginLeft: 8, color: '#6B7280', fontSize: 11 }}>
            Niche: <strong>{sugJob.nicheName}</strong>
          </span>
        )}
        <NicheGapFlag missing={sugJob?.nicheMissing} style={{ marginLeft: 8 }} />
      </div>

      {/* Filter pills + candidate count */}
      <div className="sched-sug-filters" style={{ flexWrap: 'wrap', gap: 6 }}>
        {/* Match tier filters */}
        <span style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.05em' }}>MATCH</span>
        {(() => {
          // Base-filtered list (dept + leadership + on-site office) — badge counts match what's visible
          const base = baseSuggestionFilter(candidates, job?.ServiceDesc, { includeLeadership, onSiteOfficeCode });
          return [['all', 'All'], ['client', 'Prior Client'], ['niche', 'Niche'], ['serviceline', 'Service Line'], ['none', 'No Match']].map(([key, label]) => (
          <button key={key}
            className={`sched-mgr-pill${filterTier === key ? ' sched-mgr-pill--active' : ''}`}
            onClick={() => setFilterTier(key)}
          >
            {label}
            <span style={{ marginLeft: 4, opacity: 0.7 }}>
              ({key === 'all' ? base.length : base.filter(c => c.matchTier === key).length})
            </span>
          </button>
        ));
        })()}

        {/* P18: Level filter — multi-select picklist (leadership levels appear only
            when "Include Leadership" is on). Empty selection = all levels. */}
        <span style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.05em', marginLeft: 8 }}>LEVEL</span>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <button
            className={`sched-mgr-pill${filterLevel.length ? ' sched-mgr-pill--active' : ''}`}
            onClick={() => setLevelMenuOpen(o => !o)}
            title="Filter to one or more staff levels"
          >
            {filterLevel.length === 0 ? 'All levels' : filterLevel.length === 1 ? filterLevel[0] : `${filterLevel.length} levels`}
            <span style={{ marginLeft: 4, opacity: 0.7 }}>▾</span>
          </button>
          {levelMenuOpen && (
            <>
              <div onClick={() => setLevelMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
              <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 41, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.12)', padding: 6, minWidth: 170 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 6px 6px' }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.05em' }}>LEVELS</span>
                  {filterLevel.length > 0 && (
                    <button onClick={() => setFilterLevel([])} style={{ fontSize: 10, color: BRAND.deepTeal, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>Clear</button>
                  )}
                </div>
                {levelOptions.map(opt => (
                  <label key={opt.label} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px', borderRadius: 6, cursor: 'pointer', fontSize: 12, color: '#374151' }}>
                    <input type="checkbox" checked={opt.levels.every(l => filterLevel.includes(l))} onChange={() => toggleLevelOption(opt)} />
                    {opt.label}
                  </label>
                ))}
              </div>
            </>
          )}
        </div>

        <button
          className={`sched-mgr-pill${includeLeadership ? ' sched-mgr-pill--active' : ''}`}
          style={{ marginLeft: 8 }}
          onClick={() => setIncludeLeadership(p => !p)}
          title="Include Senior Manager / Principal / Member (hidden by default)"
        >
          {includeLeadership ? '✓ Include leadership' : 'Include leadership'}
        </button>

        <button
          className={`sched-mgr-pill${showOnlyAvailable ? ' sched-mgr-pill--active' : ''}`}
          style={{ marginLeft: 8 }}
          onClick={() => setShowOnlyAvailable(p => !p)}
          title={`Hide staff booked solid (red) in every one of the ${availMeta.numWeeks} weeks shown`}
        >
          {showOnlyAvailable ? '✓ Available Only' : 'Available Only'}
        </button>

        {/* Availability window caption — tells the user which weeks the heatmaps cover */}
        <span style={{ marginLeft: 12, fontSize: 11, color: '#6B7280', display: 'inline-flex', alignItems: 'center', gap: 4 }}
          title={availMeta.windowKind === 'deadline'
            ? "Window runs from today through the job's deadline"
            : availMeta.rangeMode
              ? 'Window matches the assignment date range you opened this from'
              : 'Default window — open from a schedule entry to match its date range'}>
          <span style={{ fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.05em' }}>AVAILABILITY</span>
          {(() => {
            const { rangeMode, windowKind, spanned, numWeeks, assignStartISO, assignEndISO } = availMeta;
            // Default (no schedule entry) — just the next 5 weeks from today.
            if (!rangeMode) return <>next {numWeeks} weeks</>;
            // Opened from My Jobs / Dashboard — windowed through the job's deadline.
            if (windowKind === 'deadline') {
              // Deadline farther out than the 13-week display cap — don't imply it's at
              // week 13; just show the horizon and note the deadline is beyond it.
              if (spanned > numWeeks) return <>next {numWeeks} weeks <em style={{ marginLeft: 4, fontStyle: 'normal', color: '#9CA3AF' }}>&middot; deadline {fmtMon(assignEndISO)} is further out</em></>;
              const note = spanned < 5 ? 'showing 5-wk minimum' : null;
              return <>through {fmtMon(assignEndISO)} ({spanned} wk{spanned === 1 ? '' : 's'}){note && <em style={{ marginLeft: 4, fontStyle: 'normal', color: '#9CA3AF' }}>&middot; {note}</em>}</>;
            }
            // Round-trip with no end date — anchored to the start, default horizon.
            if (spanned == null) return <>anchored to {fmtMon(assignStartISO)} &middot; next {numWeeks} weeks</>;
            // Show the ACTUAL assignment range; note the displayed window only when it differs.
            const rangeStr = spanned <= 1
              ? `${fmtMon(assignStartISO)} (1 wk)`
              : `${fmtMon(assignStartISO)} – ${fmtMon(assignEndISO)} (${spanned} wks)`;
            const note = spanned < 5 ? 'showing 5-wk minimum' : spanned > 13 ? 'showing first 13' : null;
            return <>Assignment: {rangeStr}{note && <em style={{ marginLeft: 4, fontStyle: 'normal', color: '#9CA3AF' }}>&middot; {note}</em>}</>;
          })()}
        </span>

        {job?.IsOnSite && (
          <button
            className={`sched-mgr-pill${showAllOffices ? ' sched-mgr-pill--active' : ''}`}
            onClick={() => setShowAllOffices(p => !p)}
            title={`On-site job in ${job.Office || job.OfficeCode || 'this office'} — suggestions are limited to that office. Toggle to show all offices.`}
          >
            {showAllOffices ? '✓ All offices' : `On-site: ${job.OfficeCode || 'office'} only`}
          </button>
        )}

        <div style={{ marginLeft: 'auto', fontSize: 12, color: '#6B7280' }}>
          {filtered.length} candidate{filtered.length !== 1 ? 's' : ''}
          {selected.size > 0 && (
            returnMulti && existingPickIds.length > 0
              ? <> &middot; <strong style={{ color: '#065F46' }}>{newPickIds.length} new</strong> &middot; {existingPickIds.length} on team</>
              : <> &middot; <strong style={{ color: '#065F46' }}>{selected.size} selected</strong></>
          )}
        </div>
      </div>

      {/* P6: Assignment banner */}
      {selected.size > 0 && (
        <div className="sched-sug-assign-banner">
          {returnMulti && existingPickIds.length > 0 ? (
            <span>&#9989; {newPickIds.length > 0
              ? <>Adding {newPickIds.length}: {newPickIds.map(nameOf).join(', ')} &middot; {existingPickIds.length} already on team: {existingPickIds.map(nameOf).join(', ')}</>
              : <>{existingPickIds.length} on team: {existingPickIds.map(nameOf).join(', ')}</>}
            </span>
          ) : (
            <span>&#9989; {selected.size} staff selected: {[...selected].map(nameOf).join(', ')}</span>
          )}
          <button className="sched-assign-btn" onClick={handleConfirmAssign}>
            {returnMulti && newPickIds.length > 0
              ? `Add ${newPickIds.length} to team`
              : (returnToGrid ? `Apply ${selected.size} to schedule`
                : returnToChooser ? `Apply ${selected.size}` : 'Confirm & Schedule')} &rarr;
          </button>
        </div>
      )}

      {/* Candidate cards */}
      <div className="sched-sug-list">
        {filtered.length === 0 && (
          <div className="sched-sug-empty">
            <div style={{ fontSize: 24, marginBottom: 4 }}>&#128269;</div>
            No candidates match the current filters.
            <div style={{ fontSize: 11, color: '#D1D5DB', marginTop: 4 }}>Try adjusting the filters above.</div>
          </div>
        )}
        {filtered.map((c, idx) => {
          const isExpanded = expanded === c.StaffId;
          const isSelected = selected.has(c.StaffId);
          const totalScore = Math.round(c.totalScore || 0);
          const interests = interestsByStaff[c.StaffId] || [];

          return (
            <div key={c.StaffId}
              className={`sched-sug-card${isExpanded ? ' sched-sug-card--expanded' : ''}${idx === 0 ? ' sched-sug-card--top' : ''}${isSelected ? ' sched-sug-card--selected' : ''}`}
              onClick={() => setExpanded(isExpanded ? null : c.StaffId)}
            >
              {/* Rank */}
              <div className="sched-sug-rank">
                {idx === 0 ? '\uD83E\uDD47' : idx === 1 ? '\uD83E\uDD48' : idx === 2 ? '\uD83E\uDD49' : `#${idx + 1}`}
              </div>

              {/* Avatar */}
              <div className="sched-sug-avatar"
                style={{ background: totalScore >= 60 ? BRAND.deepTeal : totalScore >= 30 ? BRAND.oceanBlue : '#9CA3AF' }}>
                {(c.Initials || c.Name?.split(' ').map(w => w[0]).join('') || '??').toUpperCase().slice(0, 2)}
              </div>

              {/* Info */}
              <div className="sched-sug-info">
                <div className="sched-sug-name">{c.Name}</div>
                <div className="sched-sug-level">{c.Level} &middot; {c.DepartmentCode || c.Department}</div>
              </div>

              <MatchBadge tier={c.matchTier} />

              <GenTrackFlag band={bandByStaff[c.StaffId]} deptCode={c.DepartmentCode} jobBucket={jobBucket} />
              {c.interestScore > 0 && (
                <span className="sched-sug-match" style={{ background: '#FEF3C7', color: '#92400E' }}>
                  &#9733; Interest Area
                </span>
              )}

              {/* Fit score card — color adapts to score tier */}
              <div className="sched-sug-score" style={{
                background: totalScore >= 60 ? '#F0FDF4' : totalScore >= 35 ? '#EFF6FF' : totalScore >= 15 ? '#FFFBEB' : '#F9FAFB',
                borderColor: totalScore >= 60 ? '#BBF7D0' : totalScore >= 35 ? '#BFDBFE' : totalScore >= 15 ? '#FDE68A' : '#E5E7EB',
              }}>
                <div className="sched-sug-score-value" style={{
                  color: totalScore >= 60 ? BRAND.deepTeal : totalScore >= 35 ? BRAND.oceanBlue : totalScore >= 15 ? '#B45309' : '#9CA3AF'
                }}>{totalScore}</div>
                <div className="sched-sug-score-label">fit</div>
              </div>

              <div className="sched-sug-minibar">
                <div className="sched-sug-minibar-fill" style={{
                  width: `${totalScore}%`,
                  background: totalScore >= 67 ? BRAND.brightGreen : totalScore >= 33 ? BRAND.sageGreen : '#F59E0B',
                }} />
              </div>

              {/* Availability heatmap — one cell per week in the active window, dated.
                  In range mode, a bracket under the first inRangeCount cells marks the
                  actual assignment weeks (the rest are padding/context to the 5-wk min). */}
              {(() => {
                const weeks = availByStaff[c.StaffId];
                if (!weeks) return null;
                const CELL_W = 16, CELL_GAP = 3;
                // The window always starts at the assignment's first week, so the assignment
                // occupies the first min(spanned, displayed) cells. Exception: a deadline-windowed
                // view whose deadline falls beyond the 13-week display cap shows no bracket — a
                // "To deadline" mark over week 13 would falsely imply the deadline is that close.
                // Shared with the Available-Only filter so the bracket and the filter can never
                // disagree about which cells are the assignment.
                const inRangeCount = inRangeCountFor(weeks);
                const bracketW = inRangeCount > 0 ? inRangeCount * CELL_W + (inRangeCount - 1) * CELL_GAP : 0;
                return (
                  // Bracket is absolutely positioned (adds no height); pull the column up so
                  // it falls in the card's bottom padding — candidate row height is unchanged.
                  <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, flexShrink: 0, marginTop: inRangeCount > 0 ? -9 : 0 }}>
                    <span style={{ fontSize: 8, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Availability</span>
                    <div style={{ position: 'relative' }}>
                      <div style={{ display: 'flex', gap: CELL_GAP }}>
                        {weeks.map((w, wi) => {
                          const free = 100 - w.pct;
                          const bg = free >= 50 ? '#6CC24A' : free >= 20 ? '#F59E0B' : '#EF4444';
                          const inRange = wi < inRangeCount;
                          return (
                            <div key={wi} title={`Wk of ${fmtMon(w.weekStart)}: ${free}% free (${Math.round(w.capacity - w.scheduled)}h of ${w.capacity}h)`}
                              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                              <div style={{ width: CELL_W, height: 18, borderRadius: 3, background: bg, opacity: inRangeCount > 0 && !inRange ? 0.45 : 0.85 }} />
                              <span style={{ fontSize: 7, lineHeight: 1, color: inRangeCount > 0 && !inRange ? '#C9CDD3' : '#9CA3AF' }}>{fmtMD(w.weekStart)}</span>
                            </div>
                          );
                        })}
                      </div>
                      {inRangeCount > 0 && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, width: bracketW, marginTop: 1, pointerEvents: 'none' }}>
                          <div style={{ height: 3, borderLeft: `1px solid ${BRAND.deepTeal}`, borderRight: `1px solid ${BRAND.deepTeal}`, borderBottom: `1px solid ${BRAND.deepTeal}`, borderRadius: '0 0 2px 2px' }} />
                          <div style={{ fontSize: 10, lineHeight: 1, marginTop: 1, fontWeight: 700, color: BRAND.deepTeal, textAlign: 'center', whiteSpace: 'nowrap' }}>{availMeta.windowKind === 'deadline' ? 'To deadline' : 'Assignment'}</div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* P6: Select button \u2014 multi-select (dashboard flow + multi round-trip); hidden in single round-trip */}
              {(!returnToGrid || returnMulti) && (
                <button
                  className={`sched-sug-select-btn${isSelected ? ' sched-sug-select-btn--active' : ''}`}
                  onClick={(e) => toggleSelect(c.StaffId, e)}
                  title={isSelected ? 'Deselect' : 'Select for assignment'}
                >
                  {isSelected ? '\u2713' : '+'}
                </button>
              )}
              {/* Job View item 6 \u2014 SINGLE round-trip: apply this suggestion + return to the open modal */}
              {returnToGrid && !returnMulti && (
                <button
                  onClick={(e) => { e.stopPropagation(); pickStafferForGrid(c.StaffId); }}
                  title="Apply this staff suggestion and return to the schedule"
                  style={{ marginTop: 8, width: '100%', padding: '7px 10px', background: BRAND.deepTeal, color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                  Apply staff suggestion
                </button>
              )}

              {/* Expanded breakdown — full scoring formula */}
              {isExpanded && (() => {
                const wExp = scoringWeights.experience;
                const wInt = scoringWeights.interest;
                const wTotal = wExp + wInt;
                const clientPctR = Math.round(c.clientPct || 0);
                const nichePctR = Math.round(c.nichePct || 0);
                const servicePctR = Math.round(c.servicePct || 0);
                const expScoreR = Math.round(c.expScore || 0);
                const intScoreR = Math.round(c.interestScore || 0);
                return (
                  <div className="sched-sug-breakdown">
                    {/* Experience sub-scores */}
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 6 }}>Experience Score: {expScoreR}/100</div>
                    <div className="sched-sug-breakdown-row">
                      <span className="sched-sug-breakdown-label">Client Experience</span>
                      <span className="sched-sug-breakdown-level" style={{ color: pctColor(c.clientPct) }}>
                        {pctLabel(c.clientPct)}
                        {c.ClientPopSize > 0 && <span style={{ fontSize: 10, opacity: 0.7, marginLeft: 4 }}>
                          (top {Math.max(1, Math.round(100 - (c.clientPct || 0)))}% of {c.ClientPopSize})
                        </span>}
                      </span>
                      <ScoreBar value={clientPctR} max={100} color={BRAND.deepTeal} />
                    </div>
                    <div className="sched-sug-breakdown-row">
                      <span className="sched-sug-breakdown-label">Niche: {c.nicheName || sugJob?.nicheName || '\u2014'}</span>
                      <span className="sched-sug-breakdown-level" style={{ color: pctColor(c.nichePct) }}>
                        {pctLabel(c.nichePct)}
                        {c.NichePopSize > 0 && <span style={{ fontSize: 10, opacity: 0.7, marginLeft: 4 }}>
                          (top {Math.max(1, Math.round(100 - (c.nichePct || 0)))}% of {c.NichePopSize})
                        </span>}
                      </span>
                      <ScoreBar value={nichePctR} max={100} color={BRAND.oceanBlue} />
                    </div>
                    <div className="sched-sug-breakdown-row">
                      <span className="sched-sug-breakdown-label">Service Line</span>
                      <span className="sched-sug-breakdown-level" style={{ color: pctColor(c.servicePct) }}>
                        {pctLabel(c.servicePct)}
                        {c.ServicePopSize > 0 && <span style={{ fontSize: 10, opacity: 0.7, marginLeft: 4 }}>
                          (top {Math.max(1, Math.round(100 - (c.servicePct || 0)))}% of {c.ServicePopSize})
                        </span>}
                      </span>
                      <ScoreBar value={servicePctR} max={100} color={BRAND.sageGreen} />
                    </div>

                    {/* Hours context */}
                    <div style={{ marginTop: 4, fontSize: 10, color: '#9CA3AF', display: 'flex', gap: 12 }}>
                      {c.ClientHours > 0 && <span>Client: {Math.round(c.ClientHours)}h</span>}
                      {c.NicheHours > 0 && <span>Niche: {Math.round(c.NicheHours)}h</span>}
                      {c.ServiceHours > 0 && <span>Service: {Math.round(c.ServiceHours)}h</span>}
                    </div>

                    <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>
                      = ({clientPctR}&times;3 + {nichePctR}&times;2 + {servicePctR}&times;1) / 6 = <strong style={{ color: '#374151' }}>{expScoreR}</strong>
                    </div>

                    {/* Interest sub-score */}
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginTop: 12, marginBottom: 6 }}>
                      Interest Score: {intScoreR}/100
                    </div>
                    {c.interestScore > 0 ? (
                      <div className="sched-sug-breakdown-row">
                        <span className="sched-sug-breakdown-label">Niche Preference</span>
                        <span className="sched-sug-breakdown-level" style={{ color: '#D97706' }}>
                          #{c.interestRank} pick &rarr; {intScoreR} pts
                        </span>
                        <ScoreBar value={intScoreR} max={100} color="#D97706" />
                      </div>
                    ) : (
                      <div style={{ fontSize: 11, color: '#9CA3AF', fontStyle: 'italic' }}>
                        No niche preference set for {sugJob?.nicheName || 'this niche'}
                      </div>
                    )}

                    {/* Composite formula */}
                    <div style={{ marginTop: 14, padding: '10px 12px', background: '#F9FAFB', borderRadius: 8, border: '1px solid #E5E7EB' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                        Composite Score: {totalScore}/100
                      </div>
                      <div style={{ fontSize: 10, color: '#6B7280', fontFamily: 'monospace', lineHeight: 1.6 }}>
                        = (Exp&times;w<sub>exp</sub> + Interest&times;w<sub>int</sub>) / (w<sub>exp</sub>+w<sub>int</sub>)<br />
                        = ({expScoreR}&times;{wExp} + {intScoreR}&times;{wInt}) / {wTotal}<br />
                        = ({Math.round(expScoreR * wExp)} + {Math.round(intScoreR * wInt)}) / {wTotal}<br />
                        = <strong style={{ color: BRAND.deepTeal, fontSize: 12 }}>{totalScore}</strong>
                      </div>
                      <div style={{ fontSize: 9, color: '#9CA3AF', marginTop: 4 }}>
                        Weights: Experience {wExp}%, Interest {wInt}% (configurable in Admin Console)
                      </div>
                    </div>

                    {/* P7: Interests tags */}
                    {interests.length > 0 && (
                      <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #E5E7EB' }}>
                        <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 600 }}>Interests: </span>
                        {interests.map(i => (
                          <span key={i.InterestId} className="sched-emp-interest-chip" style={{ fontSize: 10, padding: '2px 8px', marginRight: 4 }}>
                            {i.InterestTag}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="sched-sug-breakdown-actions">
                      <button className="sched-assign-btn"
                        onClick={(e) => { e.stopPropagation(); navigate(`/schedule/employee/${c.StaffId}`); }}>
                        View Profile
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>
    </div>
  );
}
